import * as React from 'react';
import { useEffect, useState } from 'react';

import { ErrorPrompt } from '@opendoor/bricks/complex';
import { Box, Flex, Text } from '@opendoor/bricks/core';
import { formatValue } from '@opendoor/bricks/core/Input/helpers';
import DropdownSingleSelect from '@opendoor/bricks/core/Select/DropdownSingleSelect';
import { ISelectOption } from '@opendoor/bricks/core/Select/select.types';

import { useValidateNumberRange } from '../sellerFlow/NumberRangeInput';

interface INumberRange {
  min_price?: number;
  max_price?: number;
}

interface IProps {
  name: string;
  increments?: number;
  maxDefault?: number;
  maxLimit?: number;
  minDefault?: number;
  minLimit?: number;
  onChange: (
    key: string,
    value: any,
    hasError?: boolean | undefined,
    errorMessage?: string | undefined,
  ) => void;
  value: INumberRange | undefined;
}

const getDropdownRangeOptions = (
  minLimit?: number,
  maxLimit?: number,
  increments?: number,
): Array<ISelectOption> | undefined => {
  const answerOptions: Array<ISelectOption> = [];
  if (minLimit !== undefined && maxLimit !== undefined && increments) {
    for (let i = minLimit; i < maxLimit + 1; i = i + increments) {
      const label = formatValue(`${i}`, 'currency', 0);
      answerOptions.push({ label, value: `${i}` });
    }
  }
  return answerOptions;
};

const DropdownNumberRange: React.FC<IProps> = ({
  name,
  minLimit,
  minDefault,
  maxLimit,
  maxDefault,
  increments,
  value,
  onChange,
}) => {
  const options = getDropdownRangeOptions(minLimit, maxLimit, increments);
  // convert currency in cents to dollars for bricks input
  const [minValue, setMinValue] = useState<string | undefined>(
    value?.min_price ? `${value?.min_price / 100}` : minDefault ? `${minDefault}` : undefined,
  );
  const [maxValue, setMaxValue] = useState<string | undefined>(
    value?.max_price ? `${value?.max_price / 100}` : maxDefault ? `${maxDefault}` : undefined,
  );

  const { error } = useValidateNumberRange({
    minValue,
    maxValue,
    minLimit,
    maxLimit,
  });

  useEffect(() => {
    // multiply minValue and maxValue by 100 to get dollars in cents
    const min = minValue ? parseInt(minValue, 10) * 100 : undefined;
    const max = maxValue ? parseInt(maxValue, 10) * 100 : undefined;
    onChange(
      name,
      {
        min_price: min,
        max_price: max,
      },
      !!error,
      error,
    );
    // eslint-disable-next-line
  }, [error, minValue, maxValue]);

  return (
    <Flex flexDirection="column" mt="6" mb={[null, 5, 7]}>
      <Flex width={['100%', '420px']}>
        <Box width="100%">
          {/* TODO: @productGrowth - Add visible label */}
          <DropdownSingleSelect
            aria-labelledby="min-value"
            name="dropdown-number-range-min"
            onChange={(newValue: string | undefined) => setMinValue(newValue)}
            options={options}
            isSearchable={true}
            selectedValue={minValue ? [minValue] : undefined}
            title={
              <Text fontWeight="semibold" fontSize="s0">
                Minimum
              </Text>
            }
            flexDirection="column"
            placeholder="Any"
            styles={{ singleValue: () => ({ textAlign: 'left', left: 0, right: 0 }) }}
          />
        </Box>
        <Box px="4">
          <Text mt="45px" fontWeight="semibold" fontSize="s0">
            to
          </Text>
        </Box>
        <Box width="100%">
          {/* TODO: @productGrowth - Add visible label */}
          <DropdownSingleSelect
            aria-labelledby="max-value"
            name="dropdown-number-range-max"
            onChange={(newValue: string | undefined) => setMaxValue(newValue)}
            options={options}
            isSearchable={true}
            selectedValue={maxValue ? [maxValue] : undefined}
            title={
              <Text fontWeight="semibold" fontSize="s0">
                Maximum
              </Text>
            }
            flexDirection="column"
            placeholder="Any"
            styles={{ singleValue: () => ({ textAlign: 'left', left: 0, right: 0 }) }}
          />
        </Box>
      </Flex>
      {error && (
        <Flex mt="5">
          <ErrorPrompt message={error} />
        </Flex>
      )}
    </Flex>
  );
};

export default DropdownNumberRange;
